import { cn } from '@fruuit/ui'
import { Info, Lightbulb } from 'lucide-react'
import type { ReactNode } from 'react'
import type { HelpCalloutKind } from './types'

/**
 * The Help centre's two callouts: "Tip" (something to try) and "Good to know" (context).
 * Each is labelled in words as well as by colour. A note, not an aside: a page has many of
 * them and repeated complementary landmarks with one name aren't distinguishable.
 */
const KINDS: Record<
  HelpCalloutKind,
  { label: string; box: string; badge: string; icon: ReactNode }
> = {
  tip: {
    label: 'Tip',
    box: 'border-leaf bg-leaf-soft/60 text-[#3c4d31]',
    badge: 'border-leaf bg-leaf-soft text-[#3c4d31]',
    icon: <Lightbulb />,
  },
  know: {
    label: 'Good to know',
    box: 'border-[#e9b97f] bg-[#fdf3e7] text-[#5a3d16]',
    badge: 'border-[#e9b97f] bg-[#fbe6cc] text-[#5a3d16]',
    icon: <Info />,
  },
}

export function HelpCallout({
  kind,
  title,
  children,
  className,
}: {
  kind: HelpCalloutKind
  title?: string
  children: ReactNode
  className?: string
}) {
  const style = KINDS[kind]
  return (
    <div
      role="note"
      data-kind={kind}
      aria-label={title ? `${style.label}: ${title}` : style.label}
      className={cn(
        'help-callout rounded-md border-l-[3px] px-4 py-3.5 text-[15px] leading-relaxed break-inside-avoid',
        style.box,
        className,
      )}
    >
      <p className="flex flex-wrap items-center gap-2">
        <span
          className={cn(
            'inline-flex items-center gap-1.5 rounded-sm border px-1.5 py-px text-[11px] font-semibold uppercase tracking-[0.08em]',
            style.badge,
          )}
        >
          <span aria-hidden="true" className="inline-flex [&>svg]:size-3.5">
            {style.icon}
          </span>
          {style.label}
        </span>
        {title ? <span className="font-semibold">{title}</span> : null}
      </p>
      <div className="mt-2 flex flex-col gap-2 [&_li]:ml-4.5 [&_li]:list-disc [&_li]:pl-1 [&_ul]:flex [&_ul]:flex-col [&_ul]:gap-1">
        {children}
      </div>
    </div>
  )
}
